import React from "react";
import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import { REGARD_SERVICES, REGARD_NOTE } from "@/data/content";
import { Reveal } from "./Reveal";
import { ASavoir } from "./ASavoir";
import { Dot, DotText } from "./DotText";

const EASE = [0.22, 1, 0.36, 1];

export const RegardServices = () => {
  return (
    <div className="mt-20 md:mt-28">
      <Reveal className="max-w-2xl mb-10 md:mb-14">
        <h2 className="font-serif-display font-light text-[#4b2018] text-3xl md:text-4xl">
          Les prestations du regard
        </h2>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-8 md:gap-x-10 md:gap-y-12 max-w-4xl">
        {REGARD_SERVICES.map((item, i) => (
          <motion.div
            key={item.name}
            data-testid={`regard-service-${i}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.1, delay: (i % 2) * 0.1, ease: EASE }}
          >
            <div className="overflow-hidden rounded-md aspect-[4/3]">
              {item.image ? (
                <img
                  src={item.image}
                  alt={`${item.name} — Ninaa Beauty`}
                  loading="lazy"
                  style={{
                    objectPosition: item.imgPos || "center",
                    transform: item.imgRotate
                      ? `rotate(${item.imgRotate})`
                      : undefined,
                  }}
                  className={`h-full w-full ${
                    item.imgFit === "contain"
                      ? "object-contain bg-[#f6f1e9]"
                      : "object-cover"
                  }`}
                />
              ) : (
                <div className="h-full w-full bg-[#f6f1e9] border border-[#ece4d7] grid place-items-center text-[#b07a63]">
                  <div className="flex flex-col items-center">
                    <Camera size={24} strokeWidth={1.2} />
                    <span className="mt-2 text-[11px] tracking-[0.22em] uppercase">
                      Photo à venir
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <h3 className="font-serif-display text-xl text-[#4b2018]">
                {item.name}
              </h3>
              {item.variant && (
                <p className="mt-1 text-xs tracking-wide text-[#b07a63]">
                  <DotText text={item.variant} />
                </p>
              )}
              {item.sentence && (
                <p className="mt-2 text-sm leading-relaxed text-[#4b2018]">
                  {item.sentence}
                </p>
              )}
              {item.priceLines && (
                <div className="mt-3 space-y-1">
                  {item.priceLines.map((pl) => (
                    <p key={pl.label} className="text-sm">
                      <span className="text-[#4b2018]">{pl.label}</span>
                      <Dot />
                      <span className="text-[#944639]">{pl.price}</span>
                    </p>
                  ))}
                </div>
              )}
              {item.meta && (
                <p className="mt-3 text-sm text-[#944639]">
                  <DotText text={item.meta} />
                </p>
              )}
              {item.included && (
                <p
                  data-testid={`regard-included-${i}`}
                  className="mt-1 text-xs tracking-wide text-[#b07a63]"
                >
                  {item.included}
                </p>
              )}
              {item.options && (
                <ul className="mt-3 space-y-1">
                  {item.options.map((o) => (
                    <li key={o.label} className="text-sm text-[#944639]">
                      <span className="text-[#4b2018]">{o.label}</span>
                      <Dot />
                      {o.meta}
                    </li>
                  ))}
                </ul>
              )}
              {item.duration && (
                <p className="mt-2 text-xs text-[#b7a996]">{item.duration}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <Reveal>
        <p
          data-testid="regard-note"
          className="mt-10 max-w-2xl text-xs leading-relaxed text-[#a79a86]"
        >
          {REGARD_NOTE}
        </p>
      </Reveal>

      <Reveal className="mt-10">
        <ASavoir />
      </Reveal>
    </div>
  );
};
